import { type ReactNode } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';

import { i18n } from '@/i18n/i18n';
import { logger } from '@/lib/logger';
import { SessionProvider } from '@/state';

import { RootFallback as RootFallbackView } from './RootFallback';

function RootFallback(_props: FallbackProps) {
  return <RootFallbackView />;
}

function logRootError(error: unknown, info: { componentStack?: string | null }): void {
  logger.error('[boundary] unhandled render error', {
    error,
    componentStack: info.componentStack,
  });
}

/**
 * Composition point for every app-wide provider, in a fixed order.
 *
 * Phase 3 added SessionProvider. Providers live here rather than in `main.tsx` so tests can wrap a
 * subtree in the same stack the app uses — a provider that only exists in `main.tsx` is a provider
 * tests silently run without.
 *
 * There is no metadata provider: document title is set by `PageMeta`, and the favicon and
 * `theme-color` by `applyBranding` (ADR-030).
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary FallbackComponent={RootFallback} onError={logRootError}>
      <I18nextProvider i18n={i18n}>
        <SessionProvider>{children}</SessionProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}
